import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useAudioContext } from '@/contexts/AudioContext';

/** Sensible first-paint fallback so the main stage is never 0×0. */
function getViewportFallback() {
  if (typeof window === 'undefined') {
    return { width: 1280, height: 720 };
  }
  const width = Math.max(320, Math.floor(window.innerWidth));
  // Match h-[calc(100vh-80px)] used on the container
  const height = Math.max(200, Math.floor(window.innerHeight - 80));
  return { width, height };
}

export function LayoutManager({ children }) {
  const containerRef = useRef(null);
  const [layoutDimensions, setLayoutDimensions] = useState(getViewportFallback);
  const [isLayoutReady, setIsLayoutReady] = useState(false);

  const { isPlaylistCollapsed } = useAudioContext();

  const updateLayoutDimensions = useCallback(() => {
    const el = containerRef.current;
    if (!el) return;

    const rect = el.getBoundingClientRect();
    let width = Math.floor(rect.width);
    let height = Math.floor(rect.height);

    // If CSS hasn't resolved yet (or flex collapsed us), use viewport fallback
    if (width <= 0 || height <= 0) {
      const fallback = getViewportFallback();
      width = width > 0 ? width : fallback.width;
      height = height > 0 ? height : fallback.height;
    }

    setLayoutDimensions((prev) => {
      if (prev.width === width && prev.height === height) return prev;
      return { width, height };
    });

    if (width > 0 && height > 0) {
      setIsLayoutReady(true);
    }
  }, []);

  // Measure as soon as the container mounts; keep in sync via ResizeObserver
  useEffect(() => {
    // Immediate + rAF covers first paint before/after layout
    updateLayoutDimensions();
    const rafId = requestAnimationFrame(updateLayoutDimensions);
    const initialTimeout = setTimeout(updateLayoutDimensions, 50);

    const resizeObserver = new ResizeObserver(() => {
      requestAnimationFrame(updateLayoutDimensions);
    });

    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    const handleWindowResize = () => {
      requestAnimationFrame(updateLayoutDimensions);
    };
    window.addEventListener('resize', handleWindowResize);

    return () => {
      cancelAnimationFrame(rafId);
      clearTimeout(initialTimeout);
      resizeObserver.disconnect();
      window.removeEventListener('resize', handleWindowResize);
    };
  }, [updateLayoutDimensions]);

  // Recalculate after playlist width transition
  useEffect(() => {
    const timer = setTimeout(updateLayoutDimensions, 350);
    return () => clearTimeout(timer);
  }, [isPlaylistCollapsed, updateLayoutDimensions]);

  return (
    <div
      ref={containerRef}
      className="h-[calc(100vh-80px)] overflow-hidden"
    >
      {React.cloneElement(children, {
        layoutDimensions,
        isLayoutReady,
      })}
    </div>
  );
}